import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";
import { HostnameConfigSummary, SelectedCustomer, SelfDeliverySettings, ThrottleSetting } from "./types";

interface RawHostnameConfig {
  hostname: string;
  hostname_aliases?: string[] | null;
}

// Mirrors settingsController's synthesis of $scope.availableThrottleSettings: the customer's
// configured menu-item tags, plus two tags-shaped item classes that aren't real tags but are
// offered as checkout-limitation targets the same way.
const ITEM_CLASS_THROTTLE_SETTINGS: ThrottleSetting[] = [
  { name: "item_class_food", display_name: "Food (Item Class)" },
  { name: "item_class_drink", display_name: "Drink (Item Class)" },
];

export const useAccountSettings = (selectedCustomer?: SelectedCustomer) => {
  const customerId = selectedCustomer?.customer_id;

  const [isLoading, setIsLoading] = useState(true);
  const [minimumPaidOrderDollars, setMinimumPaidOrderDollars] = useState(1);
  const [allowFreeOrders, setAllowFreeOrders] = useState(false);
  const [allowFreeOrdersAfterPurchase, setAllowFreeOrdersAfterPurchase] = useState(false);
  const [maxDeliveryMiles, setMaxDeliveryMiles] = useState<number | null>(null);
  const [allowedZipCodes, setAllowedZipCodes] = useState<string[]>([]);
  const [tabsWorkAnywhere, setTabsWorkAnywhere] = useState(true);
  const [hostnameConfigs, setHostnameConfigs] = useState<HostnameConfigSummary[]>([]);
  const [availableThrottleSettings, setAvailableThrottleSettings] = useState<ThrottleSetting[]>([]);
  const [connectedIntegrations, setConnectedIntegrations] = useState<Record<string, unknown>>({});
  const [hasConnectedPosIntegration, setHasConnectedPosIntegration] = useState(false);

  // Refetches without touching isLoading, so a save's post-save refresh doesn't unmount the
  // page (and any open dialog) back to the loading spinner - only the initial load does that.
  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const [propertiesRes, hostnamesRes] = await Promise.all([
        axios.get("api/getOwnerProperties", { params: { customer_id: customerId } }),
        axios.get<{ hostname_configs: RawHostnameConfig[] }>("/owner/getAllHostnameConfigs", {
          params: { customer_id: customerId },
        }),
      ]);
      const properties = propertiesRes.data.owner_properties;
      setMinimumPaidOrderDollars((properties.minimum_paid_order_cents ?? 100) / 100);
      setAllowFreeOrders(Boolean(properties.allow_free_orders));
      setAllowFreeOrdersAfterPurchase(Boolean(properties.allow_free_orders_after_purchase));
      setMaxDeliveryMiles(properties.max_delivery_miles ?? null);
      setAllowedZipCodes(properties.allowed_zip_codes ?? []);
      setTabsWorkAnywhere(Boolean(properties.tabs_work_anywhere));
      // Mirrors ownerservices.js's integrationConfig.enabled = server_key in
      // owner_properties.integrations, and customer.has_connected_pos_integration.
      setConnectedIntegrations(properties.integrations || {});
      setHasConnectedPosIntegration(Boolean(properties.has_connected_pos_integration));
      setAvailableThrottleSettings([
        ...Object.entries(properties.tags || {}).map(([name, params]) => ({
          name,
          display_name: (params as { display_name: string }).display_name,
        })),
        ...ITEM_CLASS_THROTTLE_SETTINGS,
      ]);

      // Mirrors settingsController's getAllHostnameConfigs(): drop the base bbot.menu
      // hostname and sort by hostname, since this only backs the read-only domain list.
      setHostnameConfigs(
        hostnamesRes.data.hostname_configs
          .filter((config) => config.hostname !== "bbot.menu")
          .map((config) => ({
            hostname: config.hostname,
            hostnameAliases: (config.hostname_aliases || []).join(", "),
          }))
          .sort((a, b) => (a.hostname > b.hostname ? 1 : -1))
      );
    } catch (error) {
      generalErrorAlert(error, "Error retrieving account settings, please refresh.");
    }
  }, [customerId]);

  useEffect(() => {
    const initialLoad = async () => {
      setIsLoading(true);
      await load();
      setIsLoading(false);
    };
    initialLoad();
  }, [load]);

  const saveGuestCartProperties = useCallback(async () => {
    if (!customerId) return;
    try {
      await axios.post("api/setCustomerProperties", {
        customer_id: customerId,
        properties: {
          minimum_paid_order_cents: Math.round(minimumPaidOrderDollars * 100),
          allow_free_orders: allowFreeOrders,
          allow_free_orders_after_purchase: allowFreeOrdersAfterPurchase,
        },
      });
      notification.success({ message: "Guest cart properties updated successfully." });
      await load();
    } catch (error) {
      generalErrorAlert(error, "There was an error updating your guest cart properties.");
    }
  }, [customerId, minimumPaidOrderDollars, allowFreeOrders, allowFreeOrdersAfterPurchase, load]);

  // Mirrors addSelfDeliveryController's configureSelfDelivery(). onlyZipCodes tells the
  // backend to ignore settings.maxDeliveryMiles even if it's still set on the object.
  const saveSelfDelivery = useCallback(
    async (settings: SelfDeliverySettings, onlyZipCodes: boolean) => {
      if (!customerId) return false;
      try {
        await axios.post("/owner/configureSelfDelivery", {
          customer_id: customerId,
          settings,
          only_zip_codes: onlyZipCodes,
        });
        notification.success({ message: "Delivery Zone settings changed successfully!" });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble saving your new settings. Please refresh and try again.");
        return false;
      }
    },
    [customerId, load]
  );

  // Mirrors editPartyTabDomainRestrictionsController's updatePartyTabDomainRestrictions().
  const saveTabsWorkAnywhere = useCallback(
    async (nextTabsWorkAnywhere: boolean) => {
      if (!customerId) return false;
      try {
        await axios.post("/api/updatePartyTabDomainRestrictions", {
          customer_id: customerId,
          tabs_work_anywhere: nextTabsWorkAnywhere,
        });
        notification.success({ message: "Party tab domain restrictions updated successfully." });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble updating party tab domain restrictions. Please try again.");
        return false;
      }
    },
    [customerId, load]
  );

  return {
    isLoading,
    minimumPaidOrderDollars,
    setMinimumPaidOrderDollars,
    allowFreeOrders,
    setAllowFreeOrders,
    allowFreeOrdersAfterPurchase,
    setAllowFreeOrdersAfterPurchase,
    saveGuestCartProperties,
    selfDeliveryEnabled: Boolean(maxDeliveryMiles) || allowedZipCodes.length > 0,
    maxDeliveryMiles,
    allowedZipCodes,
    saveSelfDelivery,
    tabsWorkAnywhere,
    hostnameConfigs,
    saveTabsWorkAnywhere,
    availableThrottleSettings,
    connectedIntegrations,
    hasConnectedPosIntegration,
  };
};
